import type { CheckoutReviewLinePerSeatLimits } from '@/lib/ai/code-review-service/review-rate-limits';
import type { TrialUsage } from '@/lib/billing/recommended-plan';
import type { BillingInterval, PaidPlan } from '@/types/billing-plan';

import { MONTHS_PER_YEAR, SUBSCRIPTION_PRICES } from './constants';
import {
  buildAutoFixTrialMarker,
  buildTeamOverLimitMarker,
  buildUsedProFeatureSet,
  USED_IN_TRIAL_MARKER,
} from './trial-usage-markers';

import {
  DEFAULT_MAX_REVIEW_LINES_PER_SEAT as MAX_REVIEWED_LINES_PER_SEAT,
  DEFAULT_PRO_REVIEW_LINES_PER_SEAT as PRO_REVIEWED_LINES_PER_SEAT,
  DEFAULT_TEAM_REVIEW_LINES_PER_SEAT as TEAM_REVIEWED_LINES_PER_SEAT,
} from '@/lib/ai/code-review-service/review-rate-limits/schema';
import { AUTO_FIX_PLAN_ALLOWANCES, autoFixPoolFixes } from '@/lib/auto-fix-allowance/plans';
import { ULTRAREVIEW_LINE_QUOTA_MULTIPLIER } from '@/lib/ultrareview/quota-billing';
import { MAX_PLAN_USAGE_MULTIPLIER, PAID_PLANS, paidPlanSchema } from '@/types/billing-plan';

const REVIEWED_LINES_TOOLTIP =
  'Your monthly allowance per developer. Generated and ignored files don’t count. Reviewing the same code again counts again.';
/**
 * Each plan's auto-fix usage in words, never a number: the pools move with cost, and a number here
 * would be a promise.
 */
const AUTO_FIX_FEATURE_COPY: Record<PaidPlan, { label: string; tooltip?: string }> = {
  team: {
    label: 'Auto-fix for issues and CI',
    tooltip:
      'cubic fixes the issues it finds and failing CI checks, and pushes the fixes to your pull request. Only fixes count.',
  },
  // The labels say all there is to say; a tooltip would only repeat them.
  pro: { label: 'Extended auto-fix usage' },
  max: { label: 'Maximum auto-fix usage' },
};
const CODEBASE_SCANS_TOOLTIP =
  'Find bugs across your codebase, beyond PR changes. Biweekly scans with automatic fix PRs and Slack or email notifications.';

// Pro carries the Recommended badge for every buyer, whatever their trial usage suggests.
export const RECOMMENDED_PLAN: PaidPlan = 'pro';

export type PlanFeatureUsageMarker = {
  text: string;
  tone: 'warning' | 'positive';
  variant?: 'chip' | 'solid';
};

export type PlanComparisonFeature =
  | string
  | {
      label: string;
      description?: string;
      tooltip?: string;
      usageMarker?: PlanFeatureUsageMarker;
    };

export type PlanComparisonCard = {
  plan: PaidPlan;
  title: string;
  tagline: string;
  headline: string;
  priceDetail: string;
  featuresHeading: string | null;
  features: PlanComparisonFeature[];
  isRecommended: boolean;
  isHighlighted: boolean;
  ctaLabel: string;
  ctaHref?: string;
};

export const SHARED_PLAN_FEATURES: PlanComparisonFeature[] = [
  'AI PR reviews',
  {
    label: 'Ultrareviews',
    tooltip: `Deeper PR reviews that use ${ULTRAREVIEW_LINE_QUOTA_MULTIPLIER}× your review quota.`,
  },
  'Automatic PR descriptions',
  'Custom context',
];

function getPriceCopy(
  plan: PaidPlan,
  billingCycle: BillingInterval,
): {
  headline: string;
  priceDetail: string;
} {
  if (billingCycle === 'yearly') {
    return {
      headline: `$${Math.round(SUBSCRIPTION_PRICES[plan].yearly / MONTHS_PER_YEAR)}`,
      priceDetail: 'per developer / month, billed yearly',
    };
  }

  return {
    headline: `$${SUBSCRIPTION_PRICES[plan].monthly}`,
    priceDetail: 'per developer / month, billed monthly',
  };
}

function formatReviewedLines(value: number) {
  return value.toLocaleString('en-US');
}

function getReviewedLinesFeature(
  linesPerSeat: number,
  usageMarker?: PlanFeatureUsageMarker,
): PlanComparisonFeature {
  return {
    label: `${formatReviewedLines(linesPerSeat)} reviewed lines per developer`,
    tooltip: REVIEWED_LINES_TOOLTIP,
    usageMarker,
  };
}

/** A developer's monthly fixes on the plan, from the pools that pause customers; null for no limit. */
function autoFixFixesPerDeveloper(plan: PaidPlan): number | null {
  return autoFixPoolFixes(AUTO_FIX_PLAN_ALLOWANCES[plan], 1);
}

/** The plan's auto-fix line, in words; null for a plan without auto-fix. */
function getAutoFixFeature(
  plan: PaidPlan,
  usageMarker?: PlanFeatureUsageMarker,
): PlanComparisonFeature | null {
  return autoFixFixesPerDeveloper(plan) === 0
    ? null
    : { ...AUTO_FIX_FEATURE_COPY[plan], ...(usageMarker ? { usageMarker } : {}) };
}

/** Whether the plan gives a developer more fixes than `than` does. */
function hasLargerAutoFix(plan: PaidPlan, than: PaidPlan): boolean {
  const fixes = autoFixFixesPerDeveloper(plan);
  const thanFixes = autoFixFixesPerDeveloper(than);
  return thanFixes !== null && (fixes === null || fixes > thanFixes);
}

function getPlanCtaLabel(isChangeMode: boolean, isCurrentPlan: boolean): string {
  if (!isChangeMode) {
    return 'Continue';
  }
  return isCurrentPlan ? 'Current plan' : 'Select';
}

function getFeaturesHeading(plan: PaidPlan, hasAdditions: boolean): string | null {
  const plans = paidPlanSchema.options;
  const includedPlan = plans[plans.indexOf(plan) - 1];
  return includedPlan
    ? `Everything in ${PAID_PLANS[includedPlan].name}${hasAdditions ? ', plus' : ''}`
    : null;
}

const DEFAULT_PLAN_COMPARISON_REVIEW_LINE_LIMITS: CheckoutReviewLinePerSeatLimits = {
  teamLinesPerSeat: TEAM_REVIEWED_LINES_PER_SEAT,
  proLinesPerSeat: PRO_REVIEWED_LINES_PER_SEAT,
  maxLinesPerSeat: MAX_REVIEWED_LINES_PER_SEAT,
};

export function getPlanComparisonCards({
  billingCycle,
  selectedPlan,
  mode,
  currentPlan,
  trialUsage,
  reviewLineLimits = DEFAULT_PLAN_COMPARISON_REVIEW_LINE_LIMITS,
}: {
  billingCycle: BillingInterval;
  selectedPlan: PaidPlan;
  mode?: 'checkout' | 'change';
  currentPlan?: PaidPlan;
  trialUsage?: TrialUsage;
  reviewLineLimits?: CheckoutReviewLinePerSeatLimits;
}): PlanComparisonCard[] {
  const isChangeMode = mode === 'change';

  const teamOverLimitMarker = buildTeamOverLimitMarker(trialUsage);
  const usedProFeatures = buildUsedProFeatureSet(trialUsage);
  // A reviewed-line override gives every plan the same allowance, so Max claims its
  // multiple only when its allowance is actually larger.
  const maxHasLargerAllowance = reviewLineLimits.maxLinesPerSeat > reviewLineLimits.proLinesPerSeat;
  // Whether the trial used auto-fix.
  const autoFixFeatures = {
    team: getAutoFixFeature('team', buildAutoFixTrialMarker(trialUsage)),
    pro: getAutoFixFeature('pro'),
    max: getAutoFixFeature('max'),
  };
  const maxHasLargerAutoFix = hasLargerAutoFix('max', 'pro');

  const taglineByPlan: Record<PaidPlan, string> = {
    team: 'Core AI reviews',
    pro: 'Every feature',
    max: maxHasLargerAllowance
      ? `Pro with ${MAX_PLAN_USAGE_MULTIPLIER}× the review allowance`
      : 'Every feature',
  };

  // Each plan lists only what it adds to the plan before it.
  const featuresByPlan: Record<PaidPlan, PlanComparisonFeature[]> = {
    team: [
      getReviewedLinesFeature(reviewLineLimits.teamLinesPerSeat, teamOverLimitMarker),
      ...(autoFixFeatures.team ? [autoFixFeatures.team] : []),
      '5 custom review agents',
      'Auto approvals',
      'Standard PR + CLI reviews',
      { label: '5 private wikis', description: 'Monthly updates' },
      'Jira, Linear, and Asana',
      'Standard cubic analytics',
    ],
    pro: [
      getReviewedLinesFeature(reviewLineLimits.proLinesPerSeat),
      ...(autoFixFeatures.pro ? [autoFixFeatures.pro] : []),
      '10 custom review agents',
      'Faster PR + CLI reviews',
      {
        label: 'Fix with coding agents',
        usageMarker: usedProFeatures.has('fix_with_cubic') ? USED_IN_TRIAL_MARKER : undefined,
      },
      {
        label: 'Codebase scans for 3 repos',
        tooltip: CODEBASE_SCANS_TOOLTIP,
        usageMarker: usedProFeatures.has('codebase_scan') ? USED_IN_TRIAL_MARKER : undefined,
      },
      { label: '20 private wikis', description: 'Daily or weekly updates' },
      'Confluence and MCP integrations',
      {
        label: 'AI coding tracking + API',
        tooltip: 'Export PR review and AI coding metrics.',
      },
      'Premium support',
      'Shared Slack with engineering team',
    ],
    // Max lists only its larger allowances, when it has them.
    max: [
      ...(maxHasLargerAllowance ? [getReviewedLinesFeature(reviewLineLimits.maxLinesPerSeat)] : []),
      ...(maxHasLargerAutoFix && autoFixFeatures.max ? [autoFixFeatures.max] : []),
    ],
  };

  return paidPlanSchema.options.map((plan) => {
    const features = featuresByPlan[plan];
    return {
      plan,
      title: PAID_PLANS[plan].name,
      tagline: taglineByPlan[plan],
      ...getPriceCopy(plan, billingCycle),
      isRecommended: !isChangeMode && plan === RECOMMENDED_PLAN,
      isHighlighted: selectedPlan === plan,
      ctaLabel: getPlanCtaLabel(isChangeMode, currentPlan === plan),
      featuresHeading: getFeaturesHeading(plan, features.length > 0),
      features,
    };
  });
}
